import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarDays, Loader2, ArrowLeft, Check } from "lucide-react";
import { format } from "date-fns";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Seo from "@/components/Seo";

const DESK_IMG =
  "https://customer-assets.emergentagent.com/job_venue-central-2/artifacts/8y7tu9zs_Single%20desk%201.jpg";
const DESK_GALLERY = [
  "https://customer-assets.emergentagent.com/job_venue-central-2/artifacts/m6g46h3m_Singledesk%202.jpg",
  "https://customer-assets.emergentagent.com/job_venue-central-2/artifacts/clp2fkcn_Offisepace%206.jpg",
];

const PACKAGES = [
  { id: "day", label: "Dagpas", amount: 35, description: "Eén volledige dag aan een vrij bureau." },
  { id: "week", label: "Weekpas", amount: 130, description: "5 werkdagen binnen 2 weken." },
];

export default function BookDesk() {
  const [packageId, setPackageId] = useState("day");
  const [date, setDate] = useState();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company_name: "",
    vat_number: "",
    billing_address: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [availability, setAvailability] = useState(null);

  useEffect(() => {
    if (!date || packageId !== "day") {
      setAvailability(null);
      return;
    }
    const d = format(date, "yyyy-MM-dd");
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/desk/availability/${d}`);
        if (!cancelled) setAvailability(res.data);
      } catch {
        if (!cancelled) setAvailability(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [date, packageId]);

  const selected = PACKAGES.find((p) => p.id === packageId);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!date) return toast.error("Kies de datum waarop je langskomt");
    if (!form.name || !form.email) return toast.error("Naam en e-mail zijn verplicht");
    if (!form.company_name || !form.billing_address) {
      return toast.error("Vul je bedrijfsnaam en factuuradres in voor de facturatie");
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        space_slug: "single-desk",
        name: form.name,
        email: form.email,
        phone: form.phone,
        date: format(date, "yyyy-MM-dd"),
        start_time: "",
        duration: selected ? selected.label : "",
        attendees: 1,
        message: form.message,
        company_name: form.company_name,
        vat_number: form.vat_number,
        billing_address: form.billing_address,
        package_id: packageId,
      });
      setSubmitted(true);
      toast.success("Aanvraag ontvangen. We bevestigen je plek en sturen de factuur op.");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail) {
        toast.error(detail);
      } else {
        toast.error("Kon je aanvraag niet versturen. Probeer het opnieuw.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="book-desk-page">
      <Seo
        title="Flexplek & coworking in Roeselare — Noordstraat 140"
        description="Boek een flexplek of coworking-bureau in het centrum van Roeselare. Dag- en weekformules met wifi, koffie en gemeenschappelijke keuken. Max. 8 bureaus per dag."
        canonical="https://www.noordstraat140.com/book-desk"
      />
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 pt-10 pb-6">
        <Link to="/" className="overline inline-flex items-center gap-2 hover:text-[var(--accent)]"
              data-testid="back-home">
          <ArrowLeft size={14} /> Terug naar home
        </Link>
      </div>

      <section className="max-w-[1440px] mx-auto px-6 md:px-12 pb-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
          <div className="col-span-12 md:col-span-7">
            <p className="overline" style={{ color: "var(--accent)" }}>Single desk</p>
            <h1 className="font-serif font-light tracking-tighter leading-[0.95] mt-5"
                style={{ fontSize: "clamp(40px, 6.4vw, 96px)" }}
                data-testid="book-desk-title">
              Just a
              <br />
              <em className="not-italic" style={{ color: "var(--accent)" }}>desk.</em>
            </h1>
          </div>
          <div className="col-span-12 md:col-span-5">
            <p className="text-base md:text-lg leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              Nood aan enkel een bureau? Of je nu even weg wil van je
              thuiskantoor, of een tijdelijke plek nodig hebt in
              Roeselare. Wij bieden dag-, week- en maandformules voor
              single-desk verhuur.
            </p>
            <p className="overline mt-6" style={{ color: "var(--accent)" }}>Inbegrepen</p>
            <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2" data-testid="desk-included-list">
              {[
                "Wifi",
                "Desk & stoel",
                "Koffie, thee & water",
                "Collega's over de middag",
                "Gemeenschappelijke keuken",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm" style={{ color: "var(--text-primary)" }}>
                  <Check size={14} style={{ color: "var(--accent)" }} className="mt-1 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-12 pb-10">
        <div className="aspect-[16/8] overflow-hidden">
          <img src={DESK_IMG} alt="Single desk werkplek" className="w-full h-full object-cover" />
        </div>
        <div className="grid grid-cols-12 gap-4 md:gap-6 mt-4 md:mt-6">
          {DESK_GALLERY.map((src, i) => (
            <div
              key={src}
              className={i === 0 ? "col-span-12 md:col-span-7 aspect-[5/4] overflow-hidden" : "col-span-12 md:col-span-5 aspect-[5/4] overflow-hidden"}
            >
              <img src={src} alt={`Single desk — beeld ${i + 2}`} className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      </section>

      <section className="relative" style={{ backgroundColor: "var(--bg-surface)" }}>
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 md:col-span-5">
            <p className="overline" style={{ color: "var(--accent)" }}>Kies je formule</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light leading-tight mt-5 tracking-tight">
              Twee formules.
              <br />Hetzelfde mooie pand.
            </h2>
            <p className="mt-6 text-base leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              Elke formule omvat internet, water en koffie, en gebruik van
              de gemeenschappelijke keuken. Geen tokens, geen gedoe.
              Toegang vanaf 8u30 tot 18u00.
            </p>

            <div className="mt-10 space-y-3">
              {PACKAGES.map((p) => {
                const active = p.id === packageId;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPackageId(p.id)}
                    data-testid={`package-option-${p.id}`}
                    className="w-full text-left p-6 transition-all"
                    style={{
                      backgroundColor: active ? "var(--bg-base)" : "transparent",
                      border: `1px solid ${active ? "var(--accent)" : "var(--border-color)"}`,
                    }}
                  >
                    <div className="flex items-baseline justify-between gap-6">
                      <div>
                        <p className="font-serif text-2xl">{p.label}</p>
                        <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
                          {p.description}
                        </p>
                      </div>
                      <p className="font-serif text-2xl whitespace-nowrap">
                        €{p.amount}
                      </p>
                    </div>
                  </button>
                );
              })}
              <div
                className="w-full p-6"
                style={{ border: "1px dashed var(--border-color)" }}
                data-testid="package-month-note"
              >
                <div className="flex items-baseline justify-between gap-6">
                  <div>
                    <p className="font-serif text-2xl">Maandformule</p>
                    <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
                      Onbeperkte toegang op maat. {" "}
                      <a
                        href="mailto:liesbet@clementine.tv?subject=Aanvraag%20maandformule%20single%20desk"
                        style={{ color: "var(--accent)" }}
                        className="underline"
                      >
                        Neem contact op
                      </a>
                      .
                    </p>
                  </div>
                  <p className="font-serif text-lg whitespace-nowrap" style={{ color: "var(--text-secondary)" }}>
                    op aanvraag
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="col-span-12 md:col-span-7">
            {submitted ? (
              <div className="card-flat p-10" data-testid="desk-success-message" style={{ backgroundColor: "var(--bg-base)" }}>
                <p className="overline" style={{ color: "var(--accent)" }}>Aanvraag ontvangen</p>
                <h3 className="font-serif text-3xl mt-3 mb-4 font-light leading-tight">
                  Dankjewel, {form.name.split(" ")[0]}.
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                  We hebben je aanvraag voor de{" "}
                  <span style={{ color: "var(--text-primary)" }}>{selected?.label.toLowerCase()}</span>
                  {date ? (
                    <>
                      {" "}op{" "}
                      <span style={{ color: "var(--text-primary)" }}>{format(date, "EEEE d MMMM yyyy")}</span>
                    </>
                  ) : null}{" "}
                  goed ontvangen. We bevestigen je plek per e-mail op{" "}
                  <span style={{ color: "var(--text-primary)" }}>{form.email}</span> en sturen de factuur na bevestiging.
                </p>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="card-flat p-8 md:p-12"
                data-testid="desk-inquiry-form"
                style={{ backgroundColor: "var(--bg-base)" }}
              >
                <p className="overline" style={{ color: "var(--accent)" }}>Jouw boeking</p>
                <h3 className="font-serif text-3xl md:text-4xl font-light mt-3 leading-tight">
                  {selected ? selected.label : "Dagpas"} · €{selected ? selected.amount : "···"}
                </h3>
                <div className="divider mt-6 mb-8" />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  <div>
                    <label className="overline block mb-2" style={{ color: "var(--text-secondary)" }}>
                      Volledige naam
                    </label>
                    <input
                      data-testid="desk-name-input"
                      className="input-bare"
                      placeholder="Anna De Coster"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="overline block mb-2" style={{ color: "var(--text-secondary)" }}>
                      E-mail
                    </label>
                    <input
                      data-testid="desk-email-input"
                      type="email"
                      className="input-bare"
                      placeholder="anna@studio.be"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="overline block mb-2" style={{ color: "var(--text-secondary)" }}>
                      Telefoon
                    </label>
                    <input
                      data-testid="desk-phone-input"
                      className="input-bare"
                      placeholder="+32 …"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="overline block mb-2" style={{ color: "var(--text-secondary)" }}>
                      Startdatum
                    </label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          data-testid="desk-date-trigger"
                          className="input-bare text-left flex items-center justify-between"
                        >
                          <span style={{ color: date ? "var(--text-primary)" : "#a59c8e" }}>
                            {date ? format(date, "EEEE d MMMM yyyy") : "Kies de dag"}
                          </span>
                          <CalendarDays size={16} style={{ color: "var(--text-secondary)" }} />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    {availability && (
                      <p
                        className="text-xs mt-2"
                        data-testid="desk-availability"
                        style={{
                          color: availability.is_full ? "var(--accent)" : "var(--text-secondary)",
                        }}
                      >
                        {availability.is_full
                          ? "Volzet. Geen dagpassen meer beschikbaar voor deze datum."
                          : `Nog ${availability.remaining} van ${availability.capacity} dagpassen beschikbaar.`}
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-2 mt-2">
                    <p className="overline" style={{ color: "var(--accent)" }}>Facturatie</p>
                    <div className="divider mt-3 mb-2" />
                  </div>
                  <div>
                    <label className="overline block mb-2" style={{ color: "var(--text-secondary)" }}>
                      Bedrijfsnaam
                    </label>
                    <input
                      data-testid="desk-company-input"
                      className="input-bare"
                      placeholder="Atelier De Coster bv"
                      value={form.company_name}
                      onChange={(e) => update("company_name", e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="overline block mb-2" style={{ color: "var(--text-secondary)" }}>
                      BTW-nummer
                    </label>
                    <input
                      data-testid="desk-vat-input"
                      className="input-bare"
                      placeholder="BE 0000.000.000"
                      value={form.vat_number}
                      onChange={(e) => update("vat_number", e.target.value)}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="overline block mb-2" style={{ color: "var(--text-secondary)" }}>
                      Factuuradres
                    </label>
                    <textarea
                      data-testid="desk-billing-address-input"
                      className="input-bare"
                      placeholder="Straat & nummer, postcode, gemeente"
                      value={form.billing_address}
                      onChange={(e) => update("billing_address", e.target.value)}
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="overline block mb-2" style={{ color: "var(--text-secondary)" }}>
                      Bijkomende info
                    </label>
                    <textarea
                      data-testid="desk-message-input"
                      className="input-bare"
                      placeholder="Bijv. specifieke wensen, gewenste startuur…"
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                    />
                  </div>
                </div>

                <div className="mt-10 flex items-center justify-between gap-4 pt-6 border-t flex-wrap"
                     style={{ borderColor: "var(--border-color)" }}>
                  <div>
                    <p className="overline" style={{ color: "var(--text-secondary)" }}>Totaal (excl. BTW)</p>
                    <p className="font-serif text-3xl mt-1">
                      €{selected ? selected.amount.toFixed(2) : "···"}
                    </p>
                  </div>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={submitting || (availability && availability.is_full)}
                    data-testid="desk-submit-btn"
                  >
                    {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
                    {submitting
                      ? "Versturen…"
                      : (availability && availability.is_full)
                      ? "Volzet"
                      : "Reserveer & ontvang factuur"}
                  </button>
                </div>

                <p className="text-xs mt-6" style={{ color: "var(--text-secondary)" }}>
                  Betaling via factuur. We bevestigen je plek per e-mail en sturen de factuur op. Max. 8 dagpassen per dag.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
